import * as React from 'react';
import {Calendar} from 'lucide-react';
import {type DateRange, type Matcher, type PropsRange} from 'react-day-picker';
import {useTranslation} from 'react-i18next';
import {useScreenResize, useOutsideClick} from '../../hooks';
import {cn} from '../../lib/cn';
import {DEVICE} from '../../lib/device';
import {isMobileModalModeAvailable} from '../../lib/runtimeSettings';
import {AirbnbFieldTrigger} from '../field-trigger';
import {
  DEFAULT_DISPLAY_FORMAT,
  formatDate,
  toDate,
  type DateInput,
} from '../../fields/date-range-picker/dateUtils';
import {createDisabledMatchers} from '../../fields/date-range-picker/createDisabledMatchers';
import {useRangeValue} from '../../fields/date-range-picker/hooks/useRangeValue';
import {useRangeMonthSync} from '../../fields/date-range-picker/hooks/useRangeMonthSync';
import {resolveRangeSelection} from '../../fields/date-range-picker/utils/rangeSelection';
import {DateRangeCalendar} from '../../fields/date-range-picker/components/DateRangeCalendar';
import {AirbnbDateRangePopover} from './DateRangePopover';

type OpenDirection = 'up' | 'down';

type AirbnbDateRangePickerProps = {
  label: string;
  topLabel?: string;
  value?: DateRange;
  defaultValue?: DateRange;
  onChange?: (value: DateRange | undefined, name?: string) => void;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  name?: string;
  invalid?: boolean;
  error?: string;
  disabled?: boolean;
  loading?: boolean;
  optional?: boolean | string;
  tooltip?: React.ReactNode;
  helper?: React.ReactNode;
  className?: string;
  placeholder?: string;
  displayFormat?: string;
  minDate?: DateInput;
  maxDate?: DateInput;
  minDays?: number;
  maxDays?: number;
  disabledDays?: Matcher | Matcher[];
  numberOfMonths?: number;
  openDirection?: OpenDirection;
  mobileTitle?: string;
};

const AirbnbDateRangePicker = React.forwardRef<
  HTMLButtonElement,
  AirbnbDateRangePickerProps
>(function AirbnbDateRangePicker(
  {
    label,
    topLabel,
    value: externalValue,
    defaultValue,
    onChange,
    onBlur,
    name,
    invalid,
    error,
    disabled,
    loading,
    optional,
    tooltip,
    helper,
    className,
    placeholder,
    displayFormat = DEFAULT_DISPLAY_FORMAT,
    minDate,
    maxDate,
    minDays,
    maxDays,
    disabledDays,
    numberOfMonths,
    openDirection = 'down',
    mobileTitle,
  },
  ref,
) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerId = React.useId();
  const labelId = React.useId();
  const valueId = React.useId();
  const errorId = React.useId();
  const {t} = useTranslation();

  const normalizedValue = React.useMemo<DateRange | undefined>(() => {
    if (externalValue === undefined) return undefined;
    return {from: toDate(externalValue?.from), to: toDate(externalValue?.to)};
  }, [externalValue]);
  const normalizedDefaultValue = React.useMemo<DateRange | undefined>(() => {
    if (defaultValue === undefined) return undefined;
    return {from: toDate(defaultValue?.from), to: toDate(defaultValue?.to)};
  }, [defaultValue]);

  const {value, commit} = useRangeValue({
    value: normalizedValue,
    defaultValue: normalizedDefaultValue,
    onChange,
    name,
  });

  const normalizedMinDate = React.useMemo(() => toDate(minDate), [minDate]);
  const normalizedMaxDate = React.useMemo(() => toDate(maxDate), [maxDate]);
  const formatter = React.useMemo(() => formatDate(displayFormat), [displayFormat]);

  const {month, setMonth, syncMonthToValue} = useRangeMonthSync(value);

  const [isOpen, setIsOpen] = React.useState(false);
  const isMobile =
    useScreenResize(DEVICE.mobileXL).isMatch && isMobileModalModeAvailable();

  const isBlocked = Boolean(disabled) || Boolean(loading);
  const isInvalid = Boolean(invalid || error);
  const monthsToShow = numberOfMonths ?? (isMobile ? 1 : 2);

  const closeCalendar = React.useCallback(() => {
    setIsOpen(false);
    if (value?.from) setMonth(value.from);
  }, [setMonth, value?.from]);

  const openCalendar = React.useCallback(() => {
    if (isBlocked) return;
    setIsOpen(true);
  }, [isBlocked]);

  useOutsideClick({
    elementRef: containerRef,
    onOutsideClick: closeCalendar,
    isDisabled: !isOpen || isMobile,
  });

  React.useEffect(() => {
    if (isBlocked) setIsOpen(false);
  }, [isBlocked]);

  const handlePickerChange: NonNullable<PropsRange['onSelect']> = React.useCallback(
    (range, pickedDate) => {
      const {next, shouldClose} = resolveRangeSelection({
        previous: value,
        range,
        pickedDate,
      });
      syncMonthToValue(next);
      commit(next);
      if (shouldClose) closeCalendar();
    },
    [closeCalendar, commit, syncMonthToValue, value],
  );

  const disabledMatchers = React.useMemo(
    () =>
      createDisabledMatchers({
        minDate: normalizedMinDate,
        maxDate: normalizedMaxDate,
        maxDays,
        minDays,
        datesRange: value,
        disabledDays,
      }),
    [disabledDays, maxDays, minDays, normalizedMaxDate, normalizedMinDate, value],
  );

  const placeholderMask = React.useMemo(
    () => displayFormat.replace(/[a-zA-Z]/g, '0'),
    [displayFormat],
  );
  const hasFrom = Boolean(value?.from);
  const showValue = hasFrom || isOpen;
  const fromText = value?.from ? formatter(value.from) : placeholderMask;
  const toText = value?.to ? formatter(value.to) : placeholderMask;
  const slotClass = (filled: boolean) =>
    filled
      ? 'text-[var(--chekin-airbnb-ink-strong)]'
      : 'text-[var(--chekin-airbnb-gray-cool)]';

  const valueContent = showValue ? (
    <span
      id={valueId}
      className="absolute bottom-0 left-0 flex items-center gap-2 text-[16px] leading-6"
    >
      <span className={slotClass(Boolean(value?.from))}>{fromText}</span>
      <span className="text-[var(--chekin-airbnb-gray-text)]">→</span>
      <span className={slotClass(Boolean(value?.to))}>{toText}</span>
    </span>
  ) : undefined;

  const toggleCalendar = () => {
    if (isOpen) {
      closeCalendar();
      return;
    }
    openCalendar();
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isBlocked) return;
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
      openCalendar();
    }
  };

  const drawerTitle = mobileTitle ?? label;
  const drawerDescription = label ?? t('pick_date_range');

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <AirbnbFieldTrigger
        id={triggerId}
        ref={ref}
        label={label}
        topLabel={topLabel}
        labelId={labelId}
        errorId={error ? errorId : undefined}
        describedBy={error ? errorId : undefined}
        placeholder={placeholder ?? label}
        disabled={disabled}
        error={error ?? invalid}
        loading={loading}
        optional={optional}
        tooltip={tooltip}
        helper={helper}
        forceFloatingLabel={showValue}
        forceLabelText={Boolean(optional) || Boolean(tooltip)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-invalid={isInvalid}
        onClick={toggleCalendar}
        onKeyDown={handleTriggerKeyDown}
        onBlur={onBlur}
        trailingAdornment={
          <Calendar className="h-5 w-5 text-[var(--chekin-airbnb-ink)]" strokeWidth={2} />
        }
      >
        {valueContent}
      </AirbnbFieldTrigger>
      <AirbnbDateRangePopover
        isOpen={isOpen}
        isMobile={isMobile}
        openDirection={openDirection}
        drawerTitle={drawerTitle}
        drawerDescription={drawerDescription}
        onClose={closeCalendar}
      >
        <DateRangeCalendar
          value={value}
          month={month}
          onMonthChange={setMonth}
          onSelect={handlePickerChange}
          numberOfMonths={monthsToShow}
          minDate={normalizedMinDate}
          maxDate={normalizedMaxDate}
          maxDays={maxDays}
          minDays={minDays}
          autoFocus={isOpen}
          disabledMatchers={disabledMatchers}
        />
      </AirbnbDateRangePopover>
    </div>
  );
});

export {AirbnbDateRangePicker};
export type {AirbnbDateRangePickerProps};
